import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getSalesOrdersColumns } from "./columns";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import axios from "axios";
import { ltrValue, foamDensityWithTolerance, notchLabel } from "Common/constants/panelSpec";
import { useNavigate } from "react-router-dom";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import Select from "react-select";
import useSearchableOptions from "Common/hooks/useSearchableOptions";
import useSalesExpertNames from "Common/hooks/useSalesExpertNames";
import useCurrentRole from "Common/hooks/useCurrentRole";
import ConfirmAccountingPaymentModal from "Common/Components/ConfirmAccountingPaymentModal";
import {
  canRegisterPayment as canRegisterPaymentRole,
  canConfirmPaymentAccounting as canConfirmPaymentAccountingRole,
} from "Common/constants/roles";
import selectStyles from "Common/constants/selectStyles";
import ModalData from "Common/Components/ModalDate/ModalData";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import Swal from "sweetalert2";
import {
  getItemLabel,
  isSettled,
  itemStatusClass,
  itemStatusLabel,
  orderStatusClass,
  orderStatusLabel,
} from "Common/constants/proformaStatus";
import { apiErrorMessage } from "Common/constants/apiErrors";
import { accountingConfirmedAtLabel } from "Common/constants/payments";

const BASE_URL = "https://portal-api.mahanpanel.com";

// این صفحه فهرست سفارش‌هایی است که وضعیتشان به sales_order رسیده است.
const PAGE_ORDER_STATUS = "sales_order";

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const formatAmount = (value: string | number | undefined | null) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (num === undefined || num === null || isNaN(num)) return "0";
  return num.toLocaleString("en-US");
};

const toEnglishDigits = (str: string): string =>
  str
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

const getPersianDate = (offsetDays = 0) => {
  const d = new DateObject({ calendar: persian }).add(offsetDays, "days");
  return toEnglishDigits(d.format("YYYY/MM/DD").trim());
};

// ========== دیکد مشخصات فنی پانل از روی کد کالا ==========
// فقط به‌عنوان جایگزین وقتی سرور فیلد فنی متناظر را خالی برگردانده باشد.
const PANEL_TYPE_MAP: Record<string, string> = {
  "01": "دیواری",
  "02": "سقفی",
  "03": "پیچ مخفی",
  "04": "دیواری بدون درز",
  "05": "دیواری یک طرف بدون درز",
  "06": "سقفی یک طرف بدون درز",
  "07": "در",
};

const TOP_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "3": "گالوالوم",
  "4": "استیل",
  "5": "آلومینیوم",
  "6": "کاغذ-کرافت",
  "7": "فویل آلومینیوم",
  "8": "سلفون",
};

const BOTTOM_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "4": "استیل",
};

const FOAM_TYPE_MAP: Record<string, string> = {
  "1": "PUR",
  "2": "PIR",
};

const SHEET_COLOR_MAP: Record<string, string> = {
  "0": "مشکی",
  "1": "زرد",
  "2": "نارنجی",
  "3": "قرمز",
  "4": "کرم",
  "5": "آبی",
  "6": "سبز",
  "7": "طوسی",
  "9": "سفید",
};

interface DecodedPanelSpec {
  panelType: string;
  thickness: string;
  topSheet: string;
  bottomSheet: string;
  topSheetThickness: string;
  bottomSheetThickness: string;
  foamDensity: string;
  foamType: string;
  topColor: string;
  bottomColor: string;
  description: string;
}

const decodeGoodsCode = (rawCode?: string | null): DecodedPanelSpec | null => {
  if (!rawCode) return null;
  const code = toEnglishDigits(rawCode).replace(/[^0-9]/g, "");
  if (code.length < 14) return null;

  const panelTypeDigits = code.slice(0, 2);
  const thicknessDigits = code.slice(2, 4);
  const topSheetDigit = code.slice(4, 5);
  const bottomSheetDigit = code.slice(5, 6);
  const topSheetThicknessDigits = code.slice(6, 8);
  const bottomSheetThicknessDigits = code.slice(8, 10);
  const foamDensityDigits = code.slice(10, 12);
  const foamTypeDigit = code.slice(12, 13);
  const topColorDigit = code.slice(13, 14);
  const bottomColorDigit = code.length >= 15 ? code.slice(14, 15) : "";

  const panelType = PANEL_TYPE_MAP[panelTypeDigits] || panelTypeDigits;
  const thickness = String(Number(thicknessDigits));

  return {
    panelType,
    thickness,
    topSheet: TOP_SHEET_MATERIAL_MAP[topSheetDigit] || topSheetDigit,
    bottomSheet: BOTTOM_SHEET_MATERIAL_MAP[bottomSheetDigit] || bottomSheetDigit,
    topSheetThickness: ltrValue(`${(Number(topSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    bottomSheetThickness: ltrValue(`${(Number(bottomSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    foamDensity: foamDensityWithTolerance(Number(foamDensityDigits)),
    foamType: FOAM_TYPE_MAP[foamTypeDigit] || foamTypeDigit,
    topColor: SHEET_COLOR_MAP[topColorDigit] || topColorDigit,
    bottomColor: bottomColorDigit ? SHEET_COLOR_MAP[bottomColorDigit] || bottomColorDigit : "-",
    description: `${panelType} ${thickness} سانت`,
  };
};

// ===== قلم سفارش (ProformaInvoiceItemOut) =====
export interface SalesOrderItem {
  id: number;
  proforma_invoice_id?: number;
  line_number?: number;
  goods_service_id?: number;
  item_status?: string;
  goods_code?: string;
  goods_title?: string;
  goods_description?: string | null;
  quantity?: number;
  length?: string;
  width?: string;
  unit_price?: string;
  panel_type?: string | null;
  panel_thickness?: number | null;
  top_sheet_material?: string | null;
  bottom_sheet_material?: string | null;
  top_sheet_thickness?: number | null;
  bottom_sheet_thickness?: number | null;
  foam_density?: number | null;
  foam_type?: string | null;
  top_sheet_color?: string | null;
  bottom_sheet_color?: string | null;
  area?: string;
  gross_amount?: string;
  packaging_cost?: string;
  vat_amount?: string;
  net_amount?: string;
  created_at_jalali?: string;
  updated_at_jalali?: string;
  [key: string]: any;
}

// ===== سفارش (ProformaInvoiceOut / ProformaInvoiceListItem) =====
export interface SalesOrder {
  id: number;
  invoice_number: number;
  order_status: string;
  customer_id: number;
  customer_name: string;
  invoice_date: string;
  valid_until?: string;
  sale_type?: "domestic" | "domestic_without_packaging";
  currency_id?: number;
  currency_name?: string;
  total_area?: string;
  total_length?: string;
  item_count?: number;
  gross_amount?: string;
  packaging_cost?: string;
  vat_amount?: string;
  net_amount?: string;
  deposited_amount?: string;
  confirmed_deposited_amount?: string;
  remaining_balance?: string;
  sales_manager_approved?: boolean;
  ceo_approved?: boolean;
  settlement_status?: boolean;
  sales_expert_username?: string;
  sales_expert_fullname?: string;
  created_at?: string;
  created_at_jalali?: string;
  updated_at?: string;
  updated_at_jalali?: string;
  items?: SalesOrderItem[];
  [key: string]: any;
}

interface PaymentType {
  id: number;
  name: string;
}

interface Payment {
  id: number;
  proforma_invoice_id: number;
  payment_type_id: number;
  payment_type_name: string;
  amount: string;
  due_date: string;
  receipt_file_name: string | null;
  receipt_content_type: string | null;
  receipt_size: number | null;
  receipt_url: string | null;
  accounting_confirmed: boolean;
  accounting_confirmed_by_id: number | null;
  tracking_code: string | null;
  deposit_date: string | null;
  created_at: string;
  updated_at: string;
  /** زمان ثبت تأیید حسابداری (نسخه ۳). برای تأییدهای قدیمی null است. */
  accounting_confirmed_at_jalali?: string | null;
  accounting_confirmed_at?: string | null;
}

// این صفحه از نظر سفارش/فاکتور فقط نمایشگر است: طبق مستند هیچ دکمه‌ای وضعیت را مستقیماً
// تغییر نمی‌دهد. وضعیت سفارش را سرور از روی وضعیت قلم‌ها و پرداخت‌های تأییدشده محاسبه
// می‌کند و رابط فقط پاسخ سرور را بازتاب می‌دهد. ثبت پرداخت مجاز است چون زیرمنبع جدا
// (payments) است، نه ویرایش خود سفارش.
const Index = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
  }, []);
  const [reload, setReload] = useState<boolean>(false);

  const [detailsInvoice, setDetailsInvoice] = useState<SalesOrder | null>(null);
  const [detailsLoading, setDetailsLoading] = useState<boolean>(false);
  const [showItemDetailModal, setShowItemDetailModal] = useState<{
    order: SalesOrder;
    item: SalesOrderItem;
    index: number;
  } | null>(null);

  const [showPaymentsModal, setShowPaymentsModal] = useState<SalesOrder | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentToConfirm, setPaymentToConfirm] = useState<Payment | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(false);
  const [receiptPreview, setReceiptPreview] = useState<{ url: string; name: string } | null>(null);

  // ========== ثبت پرداخت ==========
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [showRegisterPayment, setShowRegisterPayment] = useState<SalesOrder | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState<boolean>(false);
  const resetPaymentForm = () => ({
    payment_type_id: "",
    amount: "",
    due_date: getPersianDate(0),
    receipt_file: null as File | null,
    tracking_code: "",
    deposit_date: getPersianDate(0),
  });
  const [paymentForm, setPaymentForm] = useState(resetPaymentForm());
  const paymentTypeSearch = useSearchableOptions(paymentTypes, (p) => p.name, (p) => String(p.id));

  // سفارش پس از تبدیل از پیش‌فاکتور معمولاً هنوز «مانده تا تسویه» دارد، پس کارشناس
  // فروش باید بتواند پرداخت‌های بعدی را همین‌جا ثبت کند. وضعیت سفارش را نمی‌تواند
  // تغییر دهد؛ صفحه از آن نظر فقط‌خواندنی می‌ماند.
  // فیلدهای مخصوص حسابداری (کد رهگیری و تاریخ واریز) همچنان با isAdmin کنترل می‌شوند.
  const { roleId, isAdmin } = useCurrentRole();
  // بند ۸: نقش تعیین‌کننده است؛ شرط تسویه‌نشدن در خودِ ستون بررسی می‌شود.
  const mayRegisterPayment = isAdmin || canRegisterPaymentRole(roleId);
  const mayConfirmAccounting = isAdmin || canConfirmPaymentAccountingRole(roleId);

  useEffect(() => {
    const fetchPaymentTypes = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/payment-types/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // انواع پرداخت صعودی بر اساس شناسه مرتب می‌شوند تا نوع پرداختِ
        // تازه‌ثبت‌شده انتهای فهرست کشویی «ثبت پرداخت» نمایش داده شود.
        setPaymentTypes(
          [...extractList(res.data)].sort(
            (a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0)
          )
        );
      } catch (error) {
        console.error("Error fetching payment types:", error);
      }
    };
    fetchPaymentTypes();
  }, []);

  // پاسخ سفارش گاهی sales_expert_fullname را مستقیم می‌دهد؛ برای مواقعی که ندهد، نگاشت
  // username → fullname از GET /users/ و GET /auth/me ساخته می‌شود.
  const { salesExpertNames, getSalesExpertName, seedFromRows } = useSalesExpertNames();

  // نام پارامتر فیلتر در API دقیقاً `order_status` است. ارسال نام اشتباه باعث می‌شود
  // سرور فیلتر را نادیده بگیرد و کل پیش‌فاکتورها در این صفحه ظاهر شوند.
  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/proforma-invoices/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: currentPage,
            size: pageSize,
            order_status: PAGE_ORDER_STATUS,
            ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
          },
        });
        const data = extractList(res.data);
        setOrders(data);
        seedFromRows(data);
        setTotalItems(res.data?.total ?? data.length);
        setTotalPages(res.data?.pages ?? 1);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching sales orders:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage("مشکلی در دریافت لیست سفارش‌های فروش رخ داده است.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [navigate, currentPage, pageSize, reload, seedFromRows, sortBy, sortOrder]);

  const fetchOrderDetail = useCallback(
    async (orderId: number): Promise<SalesOrder | null> => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/proforma-invoices/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return res.data;
      } catch (error: any) {
        console.error("Error fetching sales order detail:", error);
        if (error.response?.status === 401) navigate("/login");
        return null;
      }
    },
    [navigate]
  );

  const handleViewDetails = async (order: SalesOrder) => {
    setDetailsInvoice(order);
    setDetailsLoading(true);
    const detail = await fetchOrderDetail(order.id);
    if (detail) setDetailsInvoice(detail);
    setDetailsLoading(false);
  };

  const handleViewPayments = async (order: SalesOrder) => {
    setShowPaymentsModal(order);
    setPaymentsLoading(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.get(`${BASE_URL}/proforma-invoices/${order.id}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(extractList(res.data));
    } catch (error: any) {
      console.error("Error fetching payments:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleOpenRegisterPayment = (order: SalesOrder) => {
    setPaymentForm(resetPaymentForm());
    setShowRegisterPayment(order);
  };

  // پس از ثبت پرداخت، وضعیت سفارش و قلم‌ها ممکن است توسط سرور تغییر کند؛ فهرست و
  // مودال‌های باز با پاسخ تازه‌ی سرور جایگزین می‌شوند و هیچ انتقال خوش‌بینانه‌ای انجام نمی‌شود.
  const refreshAfterPaymentChange = async (orderId: number) => {
    window.dispatchEvent(new Event("mahan:refresh-menu-counts"));
    const detail = await fetchOrderDetail(orderId);
    if (detail) {
      setDetailsInvoice((prev) => (prev && prev.id === orderId ? detail : prev));
      setShowPaymentsModal((prev) => (prev && prev.id === orderId ? detail : prev));
    }
    setReload((prev) => !prev);
  };

  const handleRegisterPayment = async () => {
    if (!showRegisterPayment || paymentSubmitting) return;
    if (
      !paymentForm.payment_type_id ||
      !paymentForm.amount ||
      !paymentForm.due_date ||
      !paymentForm.receipt_file
    ) {
      Swal.fire("خطا", "تمام فیلدها اجباری است", "error");
      return;
    }
    if (isAdmin && (!paymentForm.tracking_code || !paymentForm.deposit_date)) {
      Swal.fire("خطا", "کد رهگیری و تاریخ واریز الزامی است", "error");
      return;
    }

    const formData = new FormData();
    formData.append("payment_type_id", paymentForm.payment_type_id);
    formData.append("amount", paymentForm.amount.replace(/,/g, ""));
    formData.append("due_date", paymentForm.due_date);
    formData.append("receipt_file", paymentForm.receipt_file);
    if (isAdmin) {
      formData.append("accounting_confirmed", "true");
      formData.append("tracking_code", paymentForm.tracking_code);
      formData.append("deposit_date", paymentForm.deposit_date);
    }

    setPaymentSubmitting(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${showRegisterPayment.id}/payments`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200 || res.status === 201) {
        const registeredFor = showRegisterPayment;
        setShowRegisterPayment(null);
        setPaymentForm(resetPaymentForm());
        if (showPaymentsModal && showPaymentsModal.id === registeredFor.id) {
          await handleViewPayments(registeredFor);
        }
        await refreshAfterPaymentChange(registeredFor.id);
        Swal.fire("عملیات موفق", "پرداخت با موفقیت ثبت شد", "success");
      }
    } catch (error: any) {
      console.error("Payment register error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
      // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
      let msg = apiErrorMessage(error, "خطا در ثبت پرداخت");
      Swal.fire("خطا", msg, "error");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // خروجی اکسل باید کل دیتاست را داشته باشد، نه فقط صفحه‌ی جاری.
  const fetchAllSalesOrders = useCallback(async () => {
    const token =
      localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
    const collected: SalesOrder[] = [];
    let page = 1;
    let pages = 1;
    do {
      const res = await axios.get(`${BASE_URL}/proforma-invoices/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          order_status: PAGE_ORDER_STATUS,
          page,
          size: 100,
          ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
        },
      });
      collected.push(...extractList(res.data));
      pages = res.data?.pages || 1;
      page += 1;
    } while (page <= pages && page <= 50); // سقف ایمنی
    return collected;
  }, [sortBy, sortOrder]);

  const columns = useMemo(
    () =>
      getSalesOrdersColumns({
        mayRegisterPayment,
        getSalesExpertName,
        handleViewDetails,
        handleViewPayments,
        handleOpenRegisterPayment,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [salesExpertNames, mayRegisterPayment]
  );

  const saleTypeLabel = (type?: string) =>
    type === "domestic_without_packaging" ? "داخلی بدون بسته‌بندی" : "داخلی با بسته‌بندی";

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="سفارش‌های فروش" title="سفارش‌های فروش" />
      {errorMessage && <div className="text-center text-red-500 mb-3">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع سفارش‌های فروش (<b className="total-Employs">{totalItems}</b>)
            </h6>
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : orders.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={orders}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={pageSize}
              customnumberfortable={10}
              // جدول این صفحه ۱۵ ستون دارد؛ در موبایل فقط ۲ ستون نشان داده می‌شود
              // و بقیه با دکمه‌ی + هر ردیف باز می‌شوند.
              customnumberformobile={2}
              exportAllRows={fetchAllSalesOrders}
              serverPagination={{
                currentPage,
                totalPages,
                onPageChange: setCurrentPage,
                pageSize,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هنوز هیچ سفارش فروشی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال جزئیات سفارش فروش ========== */}
      <Modal
        show={!!detailsInvoice}
        onHide={() => setDetailsInvoice(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[50rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16 font-bold">
            جزئیات سفارش فروش #{detailsInvoice?.invoice_number || ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto bg-slate-100 dark:bg-zink-700/40">
          {detailsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : (
            detailsInvoice && (
              <div className="space-y-4" dir="rtl">
                {/* ===== ۱. اطلاعات کلی و تأییدیه‌ها ===== */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-white dark:bg-zink-600/30 rounded-lg shadow-sm">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نام مشتری</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.customer_name || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">وضعیت سفارش</span>
                    <span
                      className={`font-medium px-2 py-0.5 rounded-full text-xs inline-block w-fit ${orderStatusClass(
                        detailsInvoice.order_status
                      )}`}
                    >
                      {orderStatusLabel(detailsInvoice.order_status)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">وضعیت تسویه</span>
                    <span className={`font-medium ${isSettled(detailsInvoice) ? 'text-green-500' : 'text-red-500'}`}>
                      {isSettled(detailsInvoice) ? 'تسویه شده' : 'تسویه نشده'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تأیید مدیر فروش</span>
                    <span className={`font-medium ${detailsInvoice.sales_manager_approved ? 'text-green-500' : 'text-red-500'}`}>
                      {detailsInvoice.sales_manager_approved ? 'تأیید شده' : 'تأیید نشده'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تأیید مدیرعامل</span>
                    <span className={`font-medium ${detailsInvoice.ceo_approved ? 'text-green-500' : 'text-red-500'}`}>
                      {detailsInvoice.ceo_approved ? 'تأیید شده' : 'تأیید نشده'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">کارشناس فروش</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {getSalesExpertName(detailsInvoice)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نوع فروش</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {saleTypeLabel(detailsInvoice.sale_type)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ارز</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.currency_name || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تعداد اقلام</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.item_count ?? (detailsInvoice.items?.length || 0)}
                    </span>
                  </div>
                </div>

                {/* ===== ۲. جمع‌ها و مبالغ (همه از پاسخ سرور) ===== */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-white dark:bg-zink-600/30 rounded-lg shadow-sm">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">کل متراژ</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.total_area)} متر مربع
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">کل طول</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.total_length)} متر
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مبلغ ناخالص</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.gross_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">هزینه بسته‌بندی</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.packaging_cost)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مالیات بر ارزش‌افزوده</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.vat_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مبلغ خالص</span>
                    <span className="font-bold text-custom-500 text-base">
                      {formatAmount(detailsInvoice.net_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مبلغ واریزی</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.deposited_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">واریزی تأییدشده</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(detailsInvoice.confirmed_deposited_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مانده بدهی</span>
                    <span className="font-medium text-red-500 dark:text-red-400">
                      {formatAmount(detailsInvoice.remaining_balance)} ریال
                    </span>
                  </div>
                </div>

                {/* طبق مستند: فقط پرداخت نقدی تأییدشده حسابداری روی این دو مقدار اثر دارد. */}
                <p className="text-xs text-slate-500 dark:text-zink-300 px-1">
                  فقط پرداخت نقدی تأییدشده توسط حسابداری در «واریزی تأییدشده» و «مانده بدهی» لحاظ می‌شود؛
                  پرداخت‌های چک یا LC در این جمع‌ها وارد نمی‌شوند.
                </p>

                {/* ===== ۳. تاریخ‌ها ===== */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-white dark:bg-zink-600/30 rounded-lg shadow-sm">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">شماره فاکتور</span>
                    <span className="font-mono font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.invoice_number || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تاریخ فاکتور</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.invoice_date || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تاریخ اعتبار</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.valid_until || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تاریخ ایجاد</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.created_at_jalali || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تاریخ آخرین بروزرسانی</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {detailsInvoice.updated_at_jalali || '-'}
                    </span>
                  </div>
                </div>

                {/* ===== ۴. فهرست کالاها با دکمه شرح کالا ===== */}
                <div className="border-t border-slate-200 dark:border-zink-500 pt-3 mt-2">
                  <h5 className="text-sm font-semibold mb-2">لیست کالاها/خدمات</h5>
                  {detailsInvoice.items && detailsInvoice.items.length > 0 ? (
                    <div className="space-y-2">
                      {detailsInvoice.items.map((item, idx) => (
                        <div
                          key={item.id ?? idx}
                          className="flex items-center justify-between p-2 bg-white dark:bg-zink-600/30 rounded-lg border border-slate-200 dark:border-zink-500 shadow-sm"
                        >
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                            <span className="font-medium">{getItemLabel(item, idx)}</span>
                            {item.item_status && (
                              <span
                                className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${itemStatusClass(
                                  item.item_status
                                )}`}
                              >
                                {itemStatusLabel(item.item_status)}
                              </span>
                            )}
                            <span className="text-xs text-slate-500 dark:text-zink-300">
                              تعداد: {item.quantity ?? 0}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-zink-300">
                              طول: {item.length ?? 0}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-zink-300">
                              عرض: {item.width ?? 0}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-zink-300">
                              قیمت واحد: {formatAmount(item.unit_price)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              setShowItemDetailModal({ order: detailsInvoice, item, index: idx })
                            }
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-custom-600 dark:text-custom-400 border border-custom-500 dark:border-custom-400 rounded-lg bg-transparent hover:bg-custom-50 dark:hover:bg-custom-900/20 transition-colors duration-200"
                          >
                            شرح کالا
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 dark:text-zink-300">هیچ کالایی ثبت نشده است.</p>
                  )}
                </div>

                <div className="text-xs text-black dark:text-zink-400 border-t border-slate-200 dark:border-zink-500 pt-2 mt-2 flex flex-wrap gap-3 justify-end">
                  <span>شناسه: {detailsInvoice.id}</span>
                  <span>شناسه مشتری: {detailsInvoice.customer_id}</span>
                </div>
              </div>
            )
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={() => setDetailsInvoice(null)}
            className="btn min-w-[9rem] px-8 py-2.5 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 hover:border-custom-600 focus:ring focus:ring-custom-100"
          >
            بستن
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== مودال شرح کالا (مشخصات کامل یک قلم) ========== */}
      <Modal
        show={!!showItemDetailModal}
        onHide={() => setShowItemDetailModal(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[46rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16 font-bold">
            {showItemDetailModal
              ? getItemLabel(showItemDetailModal.item, showItemDetailModal.index)
              : 'شرح کالا'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          {showItemDetailModal && (() => {
            const { order, item, index } = showItemDetailModal;
            // مشخصات فنی مستقیماً از ProformaInvoiceItemOut خوانده می‌شود و فقط در صورت
            // خالی بودن فیلد سرور، از رمزگشایی کد کالا به‌عنوان جایگزین استفاده می‌شود.
            const decoded = decodeGoodsCode(item.goods_code);
            const sheetMeasure = (v?: number | null, alt?: string) =>
              v !== undefined && v !== null ? ltrValue(`${(Number(v) / 100).toFixed(2)} ± 0.02`) : alt || '-';
            const spec = {
              panelType: item.panel_type || decoded?.panelType || '-',
              thickness:
                item.panel_thickness !== undefined && item.panel_thickness !== null
                  ? String(item.panel_thickness)
                  : decoded?.thickness || '-',
              topSheet: item.top_sheet_material || decoded?.topSheet || '-',
              bottomSheet: item.bottom_sheet_material || decoded?.bottomSheet || '-',
              topSheetThickness: sheetMeasure(item.top_sheet_thickness, decoded?.topSheetThickness),
              bottomSheetThickness: sheetMeasure(
                item.bottom_sheet_thickness,
                decoded?.bottomSheetThickness
              ),
              foamDensity: sheetMeasure(item.foam_density, decoded?.foamDensity),
              foamType: item.foam_type || decoded?.foamType || '-',
              topColor: item.top_sheet_color || decoded?.topColor || '-',
              bottomColor: item.bottom_sheet_color || decoded?.bottomColor || '-',
            };

            return (
              <div className="space-y-4" dir="rtl">
                {/* اطلاعات پایه کالا */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-slate-100 dark:bg-zink-600/30 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">وضعیت کالا</span>
                    <span
                      className={`font-medium px-2 py-0.5 rounded-full text-xs inline-block w-fit ${itemStatusClass(
                        item.item_status
                      )}`}
                    >
                      {itemStatusLabel(item.item_status)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">کد کالا</span>
                    <span className="font-mono font-medium text-slate-700 dark:text-zink-100">
                      {item.goods_code || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">عنوان کالا</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {item.goods_title || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">توضیحات کالا</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {item.goods_description || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">شماره ردیف</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {item.line_number ?? index + 1}
                    </span>
                  </div>
                </div>

                {/* تعداد و ابعاد */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-100 dark:bg-zink-600/30 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تعداد</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.quantity ?? '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">طول</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.length ?? '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">عرض</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.width ?? '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">فی (قیمت واحد)</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(item.unit_price)}
                    </span>
                  </div>
                </div>

                {/* مشخصات فنی پانل */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نوع پانل</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.panelType}</span>
                  </div>
                  {/* «نک‌دار رو» و «نک‌دار زیر» همین قلم؛ مستقیم از پاسخ سرور.
                      رکوردهای قدیمی فقط has_notch دارند و همان جایگزین می‌شود. */}
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نک‌دار رو</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {notchLabel(item.has_notch_top ?? item.has_notch)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نک‌دار زیر</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {notchLabel(item.has_notch_bottom ?? item.has_notch)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ضخامت پانل</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {spec.thickness !== '-' ? `${spec.thickness} سانت` : '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ورق رو</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.topSheet}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ورق پشت</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.bottomSheet}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ضخامت ورق رو</span>
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">
                      {spec.topSheetThickness}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">ضخامت ورق پشت</span>
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">
                      {spec.bottomSheetThickness}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">دانسیته فوم</span>
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">
                      {spec.foamDensity}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">نوع فوم</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.foamType}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">رنگ ورق رو</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.topColor}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">رنگ ورق زیر</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{spec.bottomColor}</span>
                  </div>
                </div>

                {/* مبالغ این قلم */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">متراژ</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(item.area)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">هزینه بسته‌بندی</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(item.packaging_cost)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مالیات بر ارزش‌افزوده</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {formatAmount(item.vat_amount)} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مبلغ خالص</span>
                    <span className="font-bold text-custom-500 text-base">
                      {formatAmount(item.net_amount)} ریال
                    </span>
                  </div>
                </div>

                <div className="text-xs text-black dark:text-zink-400 border-t border-slate-200 dark:border-zink-500 pt-2 mt-2 text-left">
                  شناسه کالا: {item.id} | شناسه سفارش: {order.id}
                </div>
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>

      {/* ========== مودال نمایش پرداخت‌ها (فقط‌خواندنی) ========== */}
      <Modal
        show={!!showPaymentsModal}
        onHide={() => {
          setShowPaymentsModal(null);
          setPayments([]);
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[40rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">
            پرداخت‌های سفارش {showPaymentsModal ? `#${showPaymentsModal.invoice_number}` : ""}
          </Modal.Title>
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                if (showPaymentsModal) handleOpenRegisterPayment(showPaymentsModal);
              }}
              className="btn bg-custom-500 text-white text-xs px-3 py-1.5"
            >
              ثبت پرداخت
            </button>
          )}
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          {paymentsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : payments.length === 0 ? (
            <p className="text-blue-400 text-center py-6">هیچ پرداختی ثبت نشده است</p>
          ) : (
            <>
              {payments.map((p) => (
                <div
                  key={p.id}
                  className="rounded-lg border border-slate-200 dark:border-zink-500 p-3 mb-3 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {p.payment_type_name} - {formatAmount(p.amount)}
                      </div>
                      <div className="text-xs text-blue-400">تاریخ واریز/سررسید: {p.due_date}</div>
                      {p.receipt_file_name && (
                        <div className="text-xs text-blue-400 mt-1 flex items-center gap-1.5">
                          <span>فایل رسید: {p.receipt_file_name}</span>
                          {p.receipt_url && (
                            <button
                              type="button"
                              onClick={() =>
                                setReceiptPreview({ url: p.receipt_url as string, name: p.receipt_file_name as string })
                              }
                              className="text-custom-500 hover:text-custom-600 underline"
                            >
                              مشاهده فایل
                            </button>
                          )}
                        </div>
                      )}
                      {p.accounting_confirmed && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ تأیید حسابداری - کد رهگیری: {p.tracking_code}
                          {/* تاریخ و زمان ثبت تأیید حسابداری، بلافاصله بعد از متن تأیید.
                              «تاریخ واریز» جداگانه نمایش داده نمی‌شود چون همان مقدار در
                              سطر «تاریخ واریز/سررسید» بالا دیده می‌شود. */}
                          {accountingConfirmedAtLabel(p) && (
                            <div className="font-semibold text-black dark:text-zink-100">
                              تاریخ و زمان ثبت حسابداری: {accountingConfirmedAtLabel(p)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div>
                      {p.accounting_confirmed ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <CheckCircle2 className="size-4" /> تأیید شده
                        </span>
                      ) : (
                        <div className="flex flex-col items-end gap-2">
                          <span className="text-xs text-blue-400">در انتظار تأیید</span>
                          {mayConfirmAccounting && showPaymentsModal && !isSettled(showPaymentsModal) && (
                            <button
                              type="button"
                              onClick={() => setPaymentToConfirm(p)}
                              className="text-xs px-2.5 py-1 rounded bg-purple-500 text-white hover:bg-purple-600"
                            >
                              تأیید حسابداری
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {showPaymentsModal && (
                <div className="mt-4 pt-3 border-t border-slate-200 dark:border-zink-500 space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex justify-between">
                      <span className="text-slate-500">جمع مبلغ واریزی</span>
                      <span className="font-medium">
                        {formatAmount(showPaymentsModal.deposited_amount)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">جمع واریزی تأییدشده</span>
                      <span className="font-medium">
                        {formatAmount(showPaymentsModal.confirmed_deposited_amount)}
                      </span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-zink-300">
                    فقط پرداخت نقدی تأییدشده توسط حسابداری در «واریزی تأییدشده» و «مانده بدهی» اثر دارد؛
                    پرداخت‌های چک یا LC در این جمع‌ها وارد نمی‌شوند.
                  </p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ========== مودال مشاهده فایل رسید ========== */}
      <Modal
        show={!!receiptPreview}
        onHide={() => setReceiptPreview(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[32rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">{receiptPreview?.name || "فایل رسید"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 flex items-center justify-center">
          {receiptPreview && (
            <img
              src={receiptPreview.url}
              alt={receiptPreview.name}
              className="max-w-full max-h-[70vh] rounded-lg object-contain"
            />
          )}
        </Modal.Body>
      </Modal>

      {/* ========== مودال ثبت پرداخت ========== */}
      <Modal
        show={showRegisterPayment !== null}
        onHide={() => {
          setShowRegisterPayment(null);
          setPaymentForm(resetPaymentForm());
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[34rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-400 hover:text-red-500"
        >
          <Modal.Title className="text-16">ثبت پرداخت جدید</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div>
              <label className="block text-base font-medium mb-1">نوع پرداخت:</label>
              <Select
                options={paymentTypeSearch.options}
                onInputChange={paymentTypeSearch.onInputChange}
                value={
                  paymentForm.payment_type_id
                    ? {
                      value: paymentForm.payment_type_id,
                      label:
                        paymentTypes.find(
                          (p) => String(p.id) === paymentForm.payment_type_id
                        )?.name || "",
                    }
                    : null
                }
                onChange={(opt: any) =>
                  setPaymentForm((prev) => ({ ...prev, payment_type_id: opt?.value || "" }))
                }
                placeholder="جست‌وجوی نوع پرداخت..."
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => "موردی یافت نشد"}
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1">مبلغ:</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-input w-full"
                value={paymentForm.amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, "");
                  if (val === "" || /^\d*$/.test(val)) {
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: val ? Number(val).toLocaleString("en-US") : "",
                    }));
                  }
                }}
                placeholder="۰"
              />
            </div>

            <ModalData
              label="تاریخ سررسید:"
              data={paymentForm.due_date}
              setData={(v) => setPaymentForm((prev) => ({ ...prev, due_date: v }))}
            />

            <div>
              <label className="block text-base font-medium mb-1">فایل رسید:</label>
              <input
                type="file"
                className="form-input w-full"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      Swal.fire("خطا", "حجم فایل نباید بیشتر از ۵ مگابایت باشد", "error");
                      e.target.value = "";
                      return;
                    }
                    setPaymentForm((prev) => ({ ...prev, receipt_file: file }));
                  }
                }}
              />
            </div>

            {isAdmin && (
              <>
                <div>
                  <label className="block text-base font-medium mb-1">کد رهگیری:</label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={paymentForm.tracking_code}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({ ...prev, tracking_code: e.target.value }))
                    }
                  />
                </div>
                <ModalData
                  label="تاریخ واریز:"
                  data={paymentForm.deposit_date}
                  setData={(v) => setPaymentForm((prev) => ({ ...prev, deposit_date: v }))}
                />
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleRegisterPayment}
            disabled={paymentSubmitting}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {paymentSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                در حال ثبت...
              </span>
            ) : (
              "ثبت پرداخت"
            )}
          </button>
        </Modal.Footer>
      </Modal>
      <ConfirmAccountingPaymentModal
        show={paymentToConfirm !== null}
        invoiceId={showPaymentsModal?.id ?? null}
        paymentId={paymentToConfirm?.id ?? null}
        onHide={() => setPaymentToConfirm(null)}
        onSuccess={() => {
          if (showPaymentsModal) void handleViewPayments(showPaymentsModal);
          setReload((value) => !value);
        }}
      />
    </React.Fragment>
  );
};

export default Index;
